import typeCheckModule from 'type-check';
import moment from 'moment';
import ipRegex from 'ip-regex';
import isUrl from 'common/util/isUrl';
import extractDateParts from 'common/util/extractDateParts';
import isCardNumber from 'common/util/isCardNumber';

const allNumbersRegex = /^\d+$/;
// NOTE(@zroyer) found at http://emailregex.com/
const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line
const serialNumberRegex = /\b[0-9A-F]{16}\b/i;
const igloohomeLockRegex = /\b[0-9A-Z]{12}\b/i;
const personNameRegex = /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/;
const streetNameRegex = /^\s*\S+(?:\s+\S+){2}/;
const citiyRegex = /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/;

export const SR_TYPE_CHECK_ASSERT_ERROR = 'SrTypeCheckAssertError';

const typeCheckCustomTypes = {
  NonEmptyObject: {
    typeOf: 'Object',
    validate: (x) => Object.keys(x).length,
  },
  NonEmptyArray: {
    typeOf: 'Array',
    validate: (x) => x.length > 0,
  },
  NonEmptyString: {
    typeOf: 'String',
    validate: (x) => !!x,
  },
  PositiveNumber: {
    typeOf: 'Number',
    validate: (x) => x > 0,
  },
  NonNegativeNumber: {
    typeOf: 'Number',
    validate: (x) => x >= 0,
  },
  DateString: {
    typeOf: 'String',
    validate: (x) => x && moment(x).isValid(),
  },
  DayString: {
    typeOf: 'String',
    validate: (x) => x && extractDateParts(x),
  },
  Phone: {
    typeOf: 'String',
    validate: (x) => x && x.length === 10 && allNumbersRegex.test(x),
  },
  Email: {
    typeOf: 'String',
    validate: (x) => x && emailRegex.test(x),
  },
  Ssn: {
    typeOf: 'String',
    validate: (x) => x && x.length === 9 && allNumbersRegex.test(x),
  },
  SupraCbs: {
    typeOf: 'String',
    validate: (x) => x && x.length === 7 && allNumbersRegex.test(x),
  },
  PersonName: {
    typeOf: 'String',
    validate: (x) => x && personNameRegex.test(x),
  },
  StreetName: {
    typeOf: 'String',
    validate: (x) => x && streetNameRegex.test(x),
  },
  City: {
    typeOf: 'String',
    validate: (x) => x && citiyRegex.test(x),
  },
  ZipCode: {
    typeOf: 'String',
    validate: (x) => {
      return x && x.length === 5 && allNumbersRegex.test(x);
    },
  },
  Moment: {
    typeOf: 'Object',
    validate: (x) => moment.isMoment(x) && x.isValid(),
  },
  SrRendererResult: {
    typeOf: 'Object',
    validate: (x) =>
      x && x.type && typeof x.type === 'string' && (x.html || x.redirectUrl || x.error),
  },
  SrFileObject: {
    typeOf: 'Object',
    validate: (x) =>
      x &&
      x.fileBlobUrl &&
      typeof x.fileBlobUrl === 'string' &&
      x.uploadTime &&
      typeof x.uploadTime === 'number' &&
      x.name &&
      typeof x.name === 'string' &&
      x.size &&
      typeof x.size === 'number' &&
      x.type &&
      typeof x.type === 'string',
  },
  SrLocatorCommissionStructure: {
    typeOf: 'Array',
    validate: (x) =>
      x.length > 0 &&
      x.every((bracket) => {
        return (
          typeof bracket.from === 'number' &&
          !isNaN(bracket.from) &&
          ((typeof bracket.to === 'number' && !isNaN(bracket.to) && bracket.to) ||
            typeof bracket.to === 'undefined') &&
          typeof bracket.locatorCut === 'number' &&
          bracket.locatorCut >= 0 &&
          bracket.locatorCut <= 1 &&
          (bracket.to ? x.filter(({ from }) => from === bracket.to).length === 1 : true)
        );
      }),
  },
  SerialNumber: {
    typeOf: 'String',
    validate: (x) => x && serialNumberRegex.test(x),
  },
  LocationPoint: {
    typeOf: 'Object',
    validate: (x) =>
      x &&
      x.locationLatitude &&
      typeof x.locationLatitude === 'number' &&
      x.locationLongitude &&
      typeof x.locationLongitude === 'number',
  },
  Point: {
    typeOf: 'Array',
    validate: (x) => x && x.length === 2 && typeof x[0] === 'number' && typeof x[1] === 'number',
  },
  Url: {
    typeOf: 'String',
    validate: isUrl,
  },
  JobOrder: {
    typeOf: 'Object',
    validate: (x) =>
      x &&
      x.listingId &&
      typeof x.listingId === 'number' &&
      Array.isArray(x.jobs) &&
      x.jobs.every((job) => job.jobTypeId && typeof job.jobTypeId === 'string'),
  },
  IgloohomeLockId: {
    typeOf: 'String',
    validate: (x) => x && igloohomeLockRegex.test(x) && x.includes('IG') && x.length === 12,
  },
  IpAddress: {
    typeOf: 'String',
    validate: (x) => x && ipRegex({ exact: true }).test(x),
  },
  PartnerBuyersAgentCommissions: {
    typeOf: 'Array',
    validate: (x) =>
      x.every(({ marketId, buyersAgentCommission }) => {
        return (
          typeof marketId === 'number' &&
          marketId > 0 &&
          typeof buyersAgentCommission === 'number' &&
          buyersAgentCommission >= 0
        );
      }),
  },
  NonSpecialCharacterString: {
    typeOf: 'String',
    validate: (x) => x && /^[a-zA-Z0-9\s]+$/.test(x),
  },
  // Credit card number validation: https://stackoverflow.com/a/30727110/1477578
  CardNumber: {
    typeOf: 'String',
    validate: isCardNumber,
  },
  GeoJson: {
    typeOf: 'Object',
    validate: (x) => {
      if (!x || typeof x !== 'object') {
        return false;
      }

      const validGeoJsonTypes = [
        'Point',
        'MultiPoint',
        'LineString',
        'MultiLineString',
        'Polygon',
        'MultiPolygon',
        'GeometryCollection',
        'Feature',
        'FeatureCollection',
      ];

      if (!validGeoJsonTypes.includes(x.type)) {
        return false;
      }

      // Basic validation for coordinates depending on the GeoJSON type
      switch (x.type) {
        case 'Point':
          return (
            Array.isArray(x.coordinates) &&
            x.coordinates.length === 2 &&
            x.coordinates.every((coord) => typeof coord === 'number')
          );
        case 'MultiPoint':
        case 'LineString':
          return (
            Array.isArray(x.coordinates) &&
            x.coordinates.every(
              (point) =>
                Array.isArray(point) &&
                point.length === 2 &&
                point.every((coord) => typeof coord === 'number'),
            )
          );
        case 'MultiLineString':
        case 'Polygon':
          return (
            Array.isArray(x.coordinates) &&
            x.coordinates.every(
              (line) =>
                Array.isArray(line) &&
                line.every(
                  (point) =>
                    Array.isArray(point) &&
                    point.length === 2 &&
                    point.every((coord) => typeof coord === 'number'),
                ),
            )
          );
        case 'MultiPolygon':
          return (
            Array.isArray(x.coordinates) &&
            x.coordinates.every(
              (polygon) =>
                Array.isArray(polygon) &&
                polygon.every(
                  (line) =>
                    Array.isArray(line) &&
                    line.every(
                      (point) =>
                        Array.isArray(point) &&
                        point.length === 2 &&
                        point.every((coord) => typeof coord === 'number'),
                    ),
                ),
            )
          );
        case 'GeometryCollection':
          return (
            Array.isArray(x.geometries) &&
            x.geometries.every((geometry) => typeCheckCustomTypes.GeoJson.validate(geometry))
          );
        case 'Feature':
          return !x.geometry || typeCheckCustomTypes.GeoJson.validate(x.geometry);
        case 'FeatureCollection':
          return (
            Array.isArray(x.features) &&
            x.features.every(
              (feature) =>
                typeof feature === 'object' && typeCheckCustomTypes.GeoJson.validate(feature),
            )
          );
        default:
          return false;
      }
    },
  },
  GeoJsonMultiPolygon: {
    typeOf: 'Object',
    validate: (x) => {
      if (!x || typeof x !== 'object' || x.type !== 'MultiPolygon') {
        return false;
      }
      return typeCheckCustomTypes.GeoJson.validate(x);
    },
  },
  GeoJsonFeatureCollection: {
    typeOf: 'Object',
    validate: (x) => {
      if (
        !x ||
        typeof x !== 'object' ||
        x.type !== 'FeatureCollection' ||
        !Array.isArray(x.features)
      ) {
        return false;
      }
      return typeCheckCustomTypes.GeoJson.validate(x);
    },
  },
};

function toDisplayString(actual) {
  return typeof actual === 'function'
    ? '(function `' + (actual.name || '(anonymous)') + '`)'
    : typeof actual === 'string'
    ? !String(actual)
      ? '(empty string)'
      : '(string `' + String(actual) + '`)'
    : Array.isArray(actual)
    ? !(actual.length > 0)
      ? '(empty array)'
      : '(array `' + String(actual) + '`)'
    : typeof actual === 'number'
    ? '(number `' + String(actual) + '`)'
    : actual === null
    ? '(null)'
    : actual === undefined
    ? '(undefined)'
    : typeof actual === 'object'
    ? '(object `' + Object.prototype.toString.apply(actual) + '`)'
    : String(actual);
}

export default function typeCheck(annotatedTypeName, toCheck, options = {}) {
  const isValid = typeCheckModule.typeCheck(annotatedTypeName, toCheck, {
    ...options,
    customTypes: {
      ...(options.customTypes || {}),
      ...typeCheckCustomTypes,
    },
  });
  if (!isValid) {
    const varName = annotatedTypeName.includes('::')
      ? annotatedTypeName.split('::')[0].trim()
      : null;
    const typeName = annotatedTypeName.split('::').slice(-1)[0].trim();

    const error = new Error(
      options.errorMessage ||
        `Expected${varName ? ' `' + varName + '`' : ''} to be ${typeName}, ` +
          `got: ${toDisplayString(toCheck)}`,
    );
    error.name = SR_TYPE_CHECK_ASSERT_ERROR;
    error.details = {
      varName,
      typeName,
      annotatedTypeName,
      toCheck,
    };
    throw error;
  }
}
